/**
 * src/lib/agent/suggestionsByContext.js — Context-aware suggestion registry.
 *
 * Phase 82-06 — Chat System Unification (wave 5, empty state).
 *
 * Maps an invocation context (`ctx`: 'global' | 'project' | 'deal' | 'task'
 * | 'person') to 4-6 starter prompts rendered as cards in the empty state.
 * Each suggestion has a `template` with optional `{entity.name}` placeholders
 * resolved at render time from the active scope. Suggestions flagged
 * `mode: 'instant'` dispatch the prompt directly via `chat.send`; without a
 * mode the prompt lands in the composer for the CEO to edit before sending.
 *
 * Consumed by EmptyStateSuggestions.jsx. Data-only module — no React.
 */

export const SUGGESTIONS = {
  global: [
    {
      id: 'focus-today',
      title: 'Focus today',
      subtitle: 'Top 3 priorities',
      icon: 'Target',
      template: 'What should I focus on today?',
      mode: 'instant',
    },
    {
      id: 'overdue-tasks',
      title: 'Overdue tasks',
      subtitle: 'By owner + project',
      icon: 'AlertTriangle',
      template: 'Show me all overdue tasks',
      mode: 'instant',
    },
    {
      id: 'stale-deals',
      title: 'Stale deals',
      subtitle: '7+ days no activity',
      icon: 'TrendingDown',
      template: 'Which deals have been stale for 7+ days?',
      mode: 'instant',
    },
    {
      id: 'team-status',
      title: 'Team status',
      subtitle: 'Who is on what',
      icon: 'Users',
      template: 'What is each team member working on right now?',
      mode: 'instant',
    },
  ],
  project: [
    {
      id: 'project-status',
      title: 'Status summary',
      subtitle: 'Progress + blockers',
      icon: 'ClipboardCheck',
      template: 'Summarize status for {project.name}',
      mode: 'instant',
    },
    {
      id: 'project-blockers',
      title: 'Blocked tasks',
      subtitle: 'What is holding progress',
      icon: 'Octagon',
      template: 'Which tasks are blocked on {project.name}?',
      mode: 'instant',
    },
    {
      id: 'project-budget',
      title: 'Budget status',
      subtitle: 'Hours + materials vs quote',
      icon: 'Euro',
      template: 'How is budget tracking on {project.name}?',
      mode: 'instant',
    },
    {
      id: 'project-nudge',
      title: 'Nudge supplier',
      subtitle: 'Chase late POs',
      icon: 'Truck',
      template: 'Draft a supplier nudge for late items on {project.name}',
    },
  ],
  deal: [
    {
      id: 'deal-objection',
      title: 'Handle objection',
      subtitle: 'Draft response',
      icon: 'MessageCircle',
      template: 'Help me respond to the last objection from {deal.name}',
    },
    {
      id: 'deal-followup',
      title: 'Follow-up draft',
      subtitle: 'Personalized email',
      icon: 'Mail',
      template: 'Draft a follow-up email for {deal.name}',
    },
    {
      id: 'deal-history',
      title: 'Activity history',
      subtitle: 'Last 30 days',
      icon: 'History',
      template: 'Show activity history for {deal.name}',
      mode: 'instant',
    },
    {
      id: 'deal-next-step',
      title: 'Next step',
      subtitle: 'Recommended move',
      icon: 'ArrowRight',
      template: 'What is the best next step for {deal.name}?',
      mode: 'instant',
    },
  ],
  task: [
    {
      id: 'task-log-time',
      title: 'Log time',
      subtitle: 'Quick time entry',
      icon: 'Timer',
      template: 'Log time on {task.name}',
    },
    {
      id: 'task-blocker',
      title: 'Mark blocked',
      subtitle: 'Set status + reason',
      icon: 'Octagon',
      template: 'Mark {task.name} as blocked because…',
    },
    {
      id: 'task-handoff',
      title: 'Handoff',
      subtitle: 'Reassign to teammate',
      icon: 'UserPlus',
      template: 'Reassign {task.name} to',
    },
    {
      id: 'task-done',
      title: 'Mark done',
      subtitle: 'Complete task',
      icon: 'CheckCircle',
      template: 'Mark {task.name} done',
      mode: 'instant',
    },
  ],
  person: [
    {
      id: 'person-workload',
      title: 'Current workload',
      subtitle: 'Active tasks',
      icon: 'List',
      template: 'What is {person.name} working on?',
      mode: 'instant',
    },
    {
      id: 'person-overdue',
      title: 'Overdue items',
      subtitle: 'Past due',
      icon: 'AlertTriangle',
      template: 'What does {person.name} have overdue?',
      mode: 'instant',
    },
    {
      id: 'person-capacity',
      title: 'Capacity',
      subtitle: 'Hours this week',
      icon: 'BarChart',
      template: 'How is {person.name} booked this week?',
      mode: 'instant',
    },
    {
      id: 'person-ping',
      title: 'Ping',
      subtitle: 'Send message',
      icon: 'Send',
      template: 'Ping {person.name} about',
    },
  ],
};

const FALLBACK_NAMES = {
  project: 'this project',
  deal: 'this deal',
  task: 'this task',
  person: 'them',
};

/**
 * Resolve the first scope entry of a given type and return its display name.
 * Scope entries expose name via `.name` (per scopeContract.toScopeEntry).
 */
function nameFromScope(scope, type) {
  if (!Array.isArray(scope)) return null;
  const entry = scope.find(e => e && e.type === type && e.name);
  return entry ? String(entry.name) : null;
}

/**
 * Substitute `{project.name}`, `{deal.name}`, `{task.name}`, `{person.name}`
 * in a template string with the matching scope entry's name. Unresolved
 * placeholders fall back to a neutral phrase ("this project" etc.).
 *
 * @param {string} tmpl
 * @param {Array<{type:string,id:string,name:string}>} scope
 * @returns {string}
 */
export function renderTemplate(tmpl, scope) {
  if (typeof tmpl !== 'string') return '';
  return tmpl.replace(/\{(project|deal|task|person)\.name\}/g, (_match, type) => {
    const resolved = nameFromScope(scope, type);
    if (resolved) return resolved;
    return FALLBACK_NAMES[type] || '';
  });
}

/**
 * Return the suggestion set for a given ctx. Unknown ctx falls back to global.
 * Always returns a non-empty array.
 *
 * @param {string} ctx
 * @returns {Array}
 */
export function getSuggestions(ctx) {
  const key = ctx && Object.prototype.hasOwnProperty.call(SUGGESTIONS, ctx) ? ctx : 'global';
  return SUGGESTIONS[key];
}

/**
 * Compute the empty-state headline for a given ctx + scope. Interpolates the
 * matching scope entity name when available.
 *
 * @param {string} ctx
 * @param {Array}  scope
 * @returns {string}
 */
export function getHeadline(ctx, scope) {
  if (ctx === 'project') {
    const name = nameFromScope(scope, 'project');
    return name ? `Ask about ${name}` : 'Ask about this project';
  }
  if (ctx === 'deal') {
    const name = nameFromScope(scope, 'deal');
    return name ? `Ask about ${name}` : 'Ask about this deal';
  }
  if (ctx === 'task') {
    const name = nameFromScope(scope, 'task');
    return name ? `Ask about ${name}` : 'Ask about this task';
  }
  if (ctx === 'person') {
    const name = nameFromScope(scope, 'person');
    return name ? `Ask about ${name}` : 'Ask about this person';
  }
  return 'What do you need?';
}
